import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Box,
  Button,
} from "@mui/material";
import styles from "../QuoteMultipleDestinationsPanel.module.css";
import TruckIconFO from "../../../icons/TruckIconFO";
import RailIconFO from "../../../icons/RailIconFO";
import {
  IDestinationExtension,
  IQuoteExtensionOld,
  IQuoteExtension,
} from "../../../types/ICompAnalysisQMD";
import { icons } from "../../../svg";
import { useNitroCAStore } from "../../../store/CompetitiveAnalysisStore";
import { useNitroStore } from "../../../store/Store";
import { useIsMobile } from "../../../hooks/useIsMobile";

interface TableProps {
  destination: IDestinationExtension;
  quotes: (IQuoteExtensionOld | IQuoteExtension)[];
}

export const MissingFreightTable: React.FC<TableProps> = ({
  destination,
  quotes,
}) => {
  const isMobile = useIsMobile();
  const [
    setOriginToTroubleshoot,
    setDestinationToTroubleshoot,
    setTroubleshootQuoteOpen,
  ] = useNitroCAStore((state) => [
    state.setOriginToTroubleshoot,
    state.setDestinationToTroubleshoot,
    state.setTroubleshootQuoteOpen,
  ]);
  const [setMode] = useNitroStore((state) => [state.setMode]);

  const uniqueQuotes = React.useMemo(() => {
    if (!isMobile) {
      return quotes;
    }

    const quoteByKey = new Map<string, IQuoteExtensionOld | IQuoteExtension>();

    quotes.forEach((quote) => {
      const rowType = quote.competitor ? "competitor" : "koch";
      const modeKey = (quote.mode || "").toUpperCase().trim();
      const originNameKey = (quote.origin?.name || "").toUpperCase().trim();
      const key = `${rowType}-${modeKey}-${originNameKey}`;

      if (!quoteByKey.has(key)) {
        quoteByKey.set(key, quote);
      }
    });

    return Array.from(quoteByKey.values());
  }, [isMobile, quotes]);
  return (
    <Box>
      <TableContainer>
        <Table>
          <TableBody>
            {uniqueQuotes.map((quote) => {
              const isKoch = !quote.competitor;
              const dotIcon = isKoch ? icons.BlueDotIcon : icons.RedDotIcon;
              const originNameClass = isKoch
                ? styles.originNameBlue
                : styles.originNameRed;
              return (
                <TableRow
                  key={`missing-freight-${
                    quote.competitor ? "competitor" : "koch"
                  }-${(quote.mode || "").toUpperCase()}-${(
                    quote.origin?.name || ""
                  ).toUpperCase()}`}
                  sx={{
                    background: "white",
                    height: "42px",
                  }}
                >
                  {/* Origin Name Cell */}
                  <TableCell
                    sx={{
                      paddingLeft: "12px",
                      width: "200px",
                      borderBottom: "none",
                    }}
                  >
                    <Box className={styles.originNameCellBox}>
                      <img
                        data-tooltip-id="dot-icon"
                        src={dotIcon}
                        alt="Dot Icon"
                        className={styles.dotIcon}
                      />
                      <span
                        className={`${styles.originName} ${originNameClass}`}
                      >
                        {quote.origin.name}
                      </span>
                    </Box>
                  </TableCell>

                  {/* Mode Cell */}
                  <TableCell sx={{ width: "135px", borderBottom: "none" }}>
                    <Box className={styles.modeCell}>
                      {quote.mode.toUpperCase() === "RAIL" ? (
                        <RailIconFO color="#F97316" />
                      ) : (
                        <TruckIconFO color="#60A5FA" />
                      )}
                      <span className={styles.modeText}>
                        {quote.mode.toUpperCase() === "TRUCK" && <>TRUCK</>}
                        {quote.mode.toUpperCase() === "RAIL" && <>RAIL</>}
                      </span>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none" }}>
                    <Button
                      sx={{
                        borderRadius: "20px",
                        border: "1px solid #E2E8F0",
                        paddingTop: "2px",
                        paddingBottom: "2px",
                        paddingLeft: "12px",
                        paddingRight: "12px",
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#2563EB",
                      }}
                      onClick={() => {
                        setOriginToTroubleshoot({
                          id: quote.origin.id,
                          name: quote.origin.name,
                          coordinate: quote.origin.coordinate,
                          isCity: quote.origin.isCity,
                        });
                        setDestinationToTroubleshoot(destination);
                        setMode(quote.mode);
                        setTroubleshootQuoteOpen(true);
                      }}
                    >
                      Review & Resolve
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
